import {Attribute} from '../../components/ObjectAttribute';
import {Json} from '../../components/JsonObjectCard';
import searchWorkOrdersRequest from "./requests/searchWorkOrdersRequest";
import getWorkOrderRequest from "./requests/getWorkOrderRequest";
import updateWorkOrderRequest from "./requests/updateWorkOrderRequest";
import addWorkOrderItemRequest from "./requests/addWorkOrderItemRequest";
import {RequestDefinitionData} from "../../components/RequestDefinition";

const workOrdersEndpoints: RequestDefinitionData[] = [
    searchWorkOrdersRequest,
    getWorkOrderRequest,
    updateWorkOrderRequest,
    addWorkOrderItemRequest,
];

export const workOrdersHeader = {
    title: 'Work Orders',
    description: 'This is an object representing a Fishbowl work order. Work orders belong to a manufacture order ' +
        'and represent the actual manufacturing, disassembly, or repair work to be performed, including the ' +
        'finished goods, raw goods to consume, and optional work instruction steps.',
    linkName: 'Work Orders',
    link: 'https://help.fishbowlinventory.com/advanced/s/article/Work-Order',
    endpoints: workOrdersEndpoints,
};

export const jsonObject: Json = {
    title: 'THE WORK ORDER OBJECT',
    json: {
        "id": 41,
        "number": "1024:1",
        "status": "Entered",
        "moItem": {
            "id": 385,
            "description": "Bike, 26 inch"
        },
        "location": {
            "id": 20,
            "name": "Manufacturing"
        },
        "locationGroup": {
            "id": 5,
            "name": "SLC"
        },
        "customer": {
            "id": 7,
            "name": "Sam Ash"
        },
        "class": {
            "id": 1,
            "name": "None"
        },
        "category": {
            "id": 12,
            "name": "Assembly"
        },
        "priority": "3-Normal",
        "quantityOrdered": 1,
        "quantityTarget": 1,
        "quantityScrapped": 0,
        "cost": "0",
        "note": "",
        "dateCreated": "2021-05-14T15:49:18.000-0600",
        "dateScheduled": "2021-05-14T15:49:08.160-0600",
        "dateScheduledToStart": "2021-05-14T15:49:08.160-0600",
        "dateStarted": null,
        "dateFinished": null,
        "lastModified": {
            "username": "admin",
            "dateLastModified": "2021-05-14T15:49:18.744-0600"
        },
        "assignedUsers": [
            {
                "id": 1,
                "name": "admin",
                "description": "Administrator"
            }
        ],
        "woItems": [
            {
                "id": 101,
                "part": {
                    "id": 80,
                    "name": "BK100",
                    "description": "New to Biking Kit"
                },
                "type": {
                    "id": 10,
                    "name": "Finished Good"
                },
                "uom": {
                    "id": 1,
                    "name": "Each",
                    "abbreviation": "ea"
                },
                "quantity": "1",
                "quantityUsed": "0",
                "quantityScrapped": "0",
                "cost": "0",
                "description": "New to Biking Kit",
                "destLocation": {
                    "id": 23,
                    "name": "Finished Goods"
                },
                "consumeItems": []
            },
            {
                "id": 102,
                "part": {
                    "id": 52,
                    "name": "PM100",
                    "description": "Entry Bike Pump"
                },
                "type": {
                    "id": 20,
                    "name": "Raw Good"
                },
                "uom": {
                    "id": 1,
                    "name": "Each",
                    "abbreviation": "ea"
                },
                "quantity": "1",
                "quantityUsed": "0",
                "quantityScrapped": "0",
                "cost": "0",
                "description": "Entry Bike Pump",
                "consumeItems": [
                    {
                        "id": 1,
                        "part": {
                            "id": 52,
                            "name": "PM100",
                            "description": "Entry Bike Pump"
                        },
                        "uom": {
                            "id": 1,
                            "name": "Each",
                            "abbreviation": "ea"
                        },
                        "quantity": "1",
                        "originalQuantity": "1",
                        "short": false
                    }
                ]
            }
        ],
        "steps": [
            {
                "id": 7,
                "name": "Assemble frame",
                "description": "Attach the wheels to the frame.",
                "url": "",
                "details": "",
                "sortOrder": 1,
                "quantityReady": 0,
                "quantityFulfilled": 0,
                "dateFirstReady": null,
                "dateLastFulfilled": null
            }
        ]
    },
};

export const workOrderAttributes: Attribute[] = [
    {
        name: 'id',
        type: 'integer',
        description: 'The work order\'s unique identification number.'
    },
    {
        name: 'number',
        type: 'string',
        description: 'The work order number.'
    },
    {
        name: 'status',
        type: 'string',
        description: 'The order status.'
    },
    {
        name: 'moItem',
        type: 'reference object',
        description: 'The manufacture order item the work order belongs to.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
            {
                name: 'description',
                type: 'string',
                description: 'The description of the manufacture order item.',
            }
        ]
    },
    {
        name: 'location',
        type: 'reference object',
        description: 'The location the work order is staged in. It always belongs to the work order\'s location group.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the object.',
            }
        ]
    },
    {
        name: 'locationGroup',
        type: 'reference object',
        description: 'The location group the order belongs to.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the object.',
            }
        ]
    },
    {
        name: 'customer',
        type: 'reference object',
        description: 'The customer/job the work order is for. Only included when the work order has one.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the object.',
            }
        ]
    },
    {
        name: 'class',
        type: 'reference object',
        description: 'The class category. Only included when the work order has one.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the object.',
            }
        ]
    },
    {
        name: 'category',
        type: 'reference object',
        description: 'The calendar category. Only included when the work order has one.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identification number.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the object.',
            }
        ]
    },
    {
        name: 'priority',
        type: 'string',
        description: 'The name of the priority level.',
    },
    {
        name: 'quantityOrdered',
        type: 'integer',
        description: 'The quantity ordered.'
    },
    {
        name: 'quantityTarget',
        type: 'integer',
        description: 'The target quantity to produce.'
    },
    {
        name: 'quantityScrapped',
        type: 'integer',
        description: 'The quantity scrapped.'
    },
    {
        name: 'cost',
        type: 'money',
        description: 'The cost of the work order.'
    },
    {
        name: 'note',
        type: 'string',
        description: 'The order\'s note field.'
    },
    {
        name: 'dateCreated',
        type: 'date',
        description: 'Timestamp of when the order was created.'
    },
    {
        name: 'dateScheduled',
        type: 'date',
        description: 'Timestamp of when the order was scheduled to be fulfilled.'
    },
    {
        name: 'dateScheduledToStart',
        type: 'date',
        description: 'Timestamp of when the order was scheduled to start.'
    },
    {
        name: 'dateStarted',
        type: 'date',
        description: 'Timestamp of when the order was started.'
    },
    {
        name: 'dateFinished',
        type: 'date',
        description: 'Timestamp of when the order was finished.'
    },
    {
        name: 'lastModified',
        type: 'object',
        description: 'Timestamp of when the order was last modified and the user that made the modifications.',
        children: [
            {
                name: 'username',
                type: 'string',
                description: 'The username of the user last modified.',
            },
            {
                name: 'dateLastModified',
                type: 'date',
                description: 'The date last modified.',
            }
        ]
    },
    {
        name: 'assignedUsers',
        type: 'list',
        description: 'The users assigned to the work order.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The user\'s unique identification number.',
            },
            {
                name: 'name',
                type: 'string',
                description: 'The username.',
            },
            {
                name: 'description',
                type: 'string',
                description: 'The user\'s full name.',
            }
        ]
    },
    {
        name: 'woItems',
        type: 'list',
        description: 'The list of work order items (finished goods, raw goods, repair goods, and notes).',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The item\'s unique identification number.'
            },
            {
                name: 'part',
                type: 'reference object',
                description: 'The part associated with the item.',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The unique identification number.'
                    },
                    {
                        name: 'name',
                        type: 'string',
                        description: 'The part number.'
                    },
                    {
                        name: 'description',
                        type: 'string',
                        description: 'The part description.'
                    }
                ]
            },
            {
                name: 'type',
                type: 'reference object',
                description: 'The work order item type (e.g. Finished Good, Raw Good, Repair Raw Good, Note).',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The unique identification number.'
                    },
                    {
                        name: 'name',
                        type: 'string',
                        description: 'The name of the type.'
                    }
                ]
            },
            {
                name: 'uom',
                type: 'reference object',
                description: 'The item\'s unit of measure.',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The unique identification number.',
                    },
                    {
                        name: 'name',
                        type: 'string',
                        description: 'The name of the object.',
                    },
                    {
                        name: 'abbreviation',
                        type: 'string',
                        description: 'The abbreviated name.',
                    }
                ]
            },
            {
                name: 'quantity',
                type: 'quantity',
                description: 'The quantity to fulfill.'
            },
            {
                name: 'quantityUsed',
                type: 'quantity',
                description: 'The quantity already used.'
            },
            {
                name: 'quantityScrapped',
                type: 'quantity',
                description: 'The quantity scrapped.'
            },
            {
                name: 'cost',
                type: 'money',
                description: 'The cost of the item.'
            },
            {
                name: 'description',
                type: 'string',
                description: 'The description of the item.'
            },
            {
                name: 'destLocation',
                type: 'reference object',
                description: 'The default destination location for finished goods.',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The unique identification number.'
                    },
                    {
                        name: 'name',
                        type: 'string',
                        description: 'The name of the location.'
                    }
                ]
            },
            {
                name: 'consumeItems',
                type: 'list',
                description: 'The items consumed to fulfill this work order item.',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The consume item\'s unique identification number.'
                    },
                    {
                        name: 'part',
                        type: 'reference object',
                        description: 'The part being consumed.',
                        children: [
                            {
                                name: 'id',
                                type: 'integer',
                                description: 'The unique identification number.'
                            },
                            {
                                name: 'name',
                                type: 'string',
                                description: 'The part number.'
                            },
                            {
                                name: 'description',
                                type: 'string',
                                description: 'The part description.'
                            }
                        ]
                    },
                    {
                        name: 'uom',
                        type: 'reference object',
                        description: 'The consume item\'s unit of measure.',
                        children: [
                            {
                                name: 'id',
                                type: 'integer',
                                description: 'The unique identification number.',
                            },
                            {
                                name: 'name',
                                type: 'string',
                                description: 'The name of the object.',
                            },
                            {
                                name: 'abbreviation',
                                type: 'string',
                                description: 'The abbreviated name.',
                            }
                        ]
                    },
                    {
                        name: 'quantity',
                        type: 'quantity',
                        description: 'The quantity consumed.'
                    },
                    {
                        name: 'originalQuantity',
                        type: 'quantity',
                        description: 'The original quantity to consume.'
                    },
                    {
                        name: 'short',
                        type: 'boolean',
                        description: 'Indicates if the consume item was short picked.'
                    }
                ]
            }
        ]
    },
    {
        name: 'steps',
        type: 'list',
        description: 'The work instruction steps. Only included when the includeSteps=true query parameter is provided.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The step\'s unique identification number.'
            },
            {
                name: 'name',
                type: 'string',
                description: 'The name of the step.'
            },
            {
                name: 'description',
                type: 'string',
                description: 'The description of the step.'
            },
            {
                name: 'url',
                type: 'string',
                description: 'An optional url link for the step.'
            },
            {
                name: 'details',
                type: 'string',
                description: 'Additional details for the step.'
            },
            {
                name: 'sortOrder',
                type: 'integer',
                description: 'The position in the sort order.'
            },
            {
                name: 'quantityReady',
                type: 'integer',
                description: 'The quantity ready for the step.'
            },
            {
                name: 'quantityFulfilled',
                type: 'integer',
                description: 'The quantity fulfilled for the step.'
            },
            {
                name: 'dateFirstReady',
                type: 'date',
                description: 'Timestamp of when the step was first ready.'
            },
            {
                name: 'dateLastFulfilled',
                type: 'date',
                description: 'Timestamp of when the step was last fulfilled.'
            }
        ]
    },
];
